import React, { useState, useRef, useEffect } from "react";
import { nanoid } from "nanoid";
import api from "../api";

/**
 * Same fingerprint the agent computes server-side. Lets a merge card refuse to
 * apply a plan that was built against a lesson which has since changed.
 */
const animationSignature = (animation = []) => {
  let chars = 0;
  const walk = (node) => {
    if (!node || typeof node !== "object") return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (typeof node.content === "string") chars += node.content.length;
    if (node.subSections) walk(node.subSections);
    if (node.miniSections) walk(node.miniSections);
  };
  walk(animation);
  return `${(animation || []).length}:${chars}`;
};

/**
 * Point copied image/animation blocks at this lesson's own S3 folder.
 */
const rewriteMediaUrls = (value, map) => {
  if (Array.isArray(value)) return value.map((item) => rewriteMediaUrls(item, map));
  if (value && typeof value === "object") {
    const out = {};
    for (const [key, val] of Object.entries(value)) {
      out[key] = rewriteMediaUrls(val, map);
    }
    return out;
  }
  if (typeof value === "string" && map[value]) return map[value];
  return value;
};

/**
 * AgentChatPanel
 * A floating chat drawer that sends natural-language instructions to the LLM
 * tool-calling agent and immediately applies changes to the lesson/quiz state.
 *
 * Props:
 *  isOpen            boolean
 *  onClose           () => void
 *  lesson            current lesson object
 *  quizzes           current quizzes array
 *  onLessonUpdate    (updatedLesson) => void
 *  onQuizzesUpdate   (updatedQuizzes) => void
 *  onRenumberApplied (plan) => void
 *  onLabelRenameApplied (plan) => void
 *  onDeleteApplied (plan) => void
 *  hasUnsavedChanges boolean
 *  selectedCourse    course object (for modelName/collectionName)
 *  allLessons        flat array of lesson metadata (for prev/next context)
 *  imageTheme        string (image generation theme)
 */
const AgentChatPanel = ({
  isOpen,
  onClose,
  lesson,
  quizzes,
  onLessonUpdate,
  onQuizzesUpdate,
  onRenumberApplied,
  onLabelRenameApplied,
  onDeleteApplied,
  onCourseEditConfirmed,
  onCourseEditStop,
  hasUnsavedChanges = false,
  selectedCourse,
  allLessons = [],
  imageTheme,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [includePrev, setIncludePrev] = useState(false);
  const [includeNext, setIncludeNext] = useState(false);
  // Stack of { lesson, quizzes } snapshots for undo
  const [undoStack, setUndoStack] = useState([]);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const drawerRef = useRef(null);
  // Id of the run currently in flight, so we can ask the server to cancel it.
  const activeRunIdRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Close on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (isOpen && drawerRef.current && !drawerRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [isOpen, onClose]);

  const getAdjacentLessons = () => {
    if (!lesson || !allLessons.length) return { prevLesson: null, nextLesson: null };
    const idx = allLessons.findIndex((l) => l.sectionNumber === lesson.sectionNumber);
    return {
      prevLesson: idx > 0 ? allLessons[idx - 1] : null,
      nextLesson: idx >= 0 && idx < allLessons.length - 1 ? allLessons[idx + 1] : null,
    };
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isThinking) return;

    const userMsg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsThinking(true);

    // Snapshot the editor before this request. Instant edits keep it on the
    // undo stack. Pending plans (renumber / rename / merge) pop it because
    // nothing has changed locally yet — a merge restashes it on Confirm.
    const snapshot = {
      lesson: JSON.parse(JSON.stringify(lesson)),
      quizzes: JSON.parse(JSON.stringify(quizzes)),
    };
    setUndoStack((prev) => [...prev, snapshot]);

    const { prevLesson, nextLesson } = getAdjacentLessons();
    const runId = `run-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    activeRunIdRef.current = runId;

    try {
      // Build chat history from previous messages (exclude the current one just added,
      // cap at last 20 to keep token usage reasonable)
      const historySnapshot = messages.slice(-20).map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const response = await api.post("/api/chat-lesson-edit", {
        runId,
        userMessage: text,
        chatHistory: historySnapshot,
        lesson,
        quizzes,
        prevLesson: includePrev ? prevLesson : undefined,
        nextLesson: includeNext ? nextLesson : undefined,
        imageTheme,
        courseTitle: selectedCourse?.courseTitle,
        modelName: selectedCourse?.courseTopicsModelName,
        collectionName: selectedCourse?.courseTopicsCollectionName,
        quizModelName: selectedCourse?.courseExamplesModelName,
        quizCollectionName: selectedCourse?.courseExamplesCollectionName,
      });

      const {
        updatedLesson,
        updatedQuizzes,
        assistantMessage,
        pendingRenumber,
        pendingLabelRename,
        pendingDelete,
        pendingImport,
        pendingCourseEdit,
        stopped,
      } = response.data;

      const hasPendingPlan =
        (pendingRenumber && pendingRenumber.ok)
        || (pendingLabelRename && pendingLabelRename.ok)
        || (pendingDelete && pendingDelete.ok)
        || !!pendingImport
        || (pendingCourseEdit && pendingCourseEdit.ok);

      if (hasPendingPlan) {
        setUndoStack((prev) => prev.slice(0, -1));
      } else {
        if (updatedLesson) onLessonUpdate(updatedLesson);
        if (updatedQuizzes) onQuizzesUpdate(updatedQuizzes);
      }

      const displayMessage = assistantMessage || "Done.";
      setMessages((prev) => [
        ...prev,
        {
          role: stopped ? "system" : "assistant",
          content: displayMessage,
          ...(pendingRenumber && pendingRenumber.ok
            ? { renumberPlan: pendingRenumber, renumberStatus: "pending" }
            : {}),
          ...(pendingLabelRename && pendingLabelRename.ok
            ? { labelRenamePlan: pendingLabelRename, labelRenameStatus: "pending" }
            : {}),
          ...(pendingDelete && pendingDelete.ok
            ? { deletePlan: pendingDelete, deleteStatus: "pending" }
            : {}),
          ...(pendingImport && pendingImport.ok
            ? {
                importPlan: pendingImport,
                importStatus: "pending",
                preMergeSnapshot: snapshot,
              }
            : {}),
          ...(pendingCourseEdit && pendingCourseEdit.ok
            ? { courseEditPlan: pendingCourseEdit, courseEditStatus: "pending" }
            : {}),
        },
      ]);
    } catch (err) {
      console.error("Agent error:", err);
      const errMsg =
        err.response?.data?.error || err.message || "Something went wrong.";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Error: ${errMsg}`, isError: true },
      ]);
      // Pop the snapshot we just saved since changes weren't applied
      setUndoStack((prev) => prev.slice(0, -1));
    } finally {
      activeRunIdRef.current = null;
      setIsThinking(false);
      setIsStopping(false);
    }
  };

  // Ask the server to cancel the in-flight run. The original request stays open,
  // so any edits the agent already applied still come back and get kept.
  const handleStop = async () => {
    const runId = activeRunIdRef.current;
    if (!runId || isStopping) return;
    setIsStopping(true);
    try {
      await api.post("/api/chat-lesson-edit/stop", { runId });
    } catch (err) {
      console.error("Stop request failed:", err);
      setMessages((prev) => [
        ...prev,
        { role: "system", content: "Could not reach the server to stop the run." },
      ]);
      setIsStopping(false);
    }
  };

  const courseTitlePath = selectedCourse?.courseTitle
    ? selectedCourse.courseTitle.replace(/\s/g, "")
    : "";

  const handleConfirmRenumber = async (messageIndex) => {
    const target = messages[messageIndex];
    const plan = target?.renumberPlan;
    if (!plan || !selectedCourse || target.renumberStatus !== "pending") return;

    setMessages((prev) =>
      prev.map((m, i) => (i === messageIndex ? { ...m, renumberStatus: "applying" } : m))
    );

    try {
      const response = await api.post(`/${courseTitlePath}/renumberLesson`, {
        modelName: selectedCourse.courseTopicsModelName,
        collectionName: selectedCourse.courseTopicsCollectionName,
        quizModelName: selectedCourse.courseExamplesModelName,
        quizCollectionName: selectedCourse.courseExamplesCollectionName,
        oldSectionNumber: plan.move.oldSectionNumber,
        newSectionNumber: plan.move.newSectionNumber,
      });
      setMessages((prev) => [
        ...prev.map((m, i) =>
          i === messageIndex ? { ...m, renumberStatus: "confirmed" } : m
        ),
        {
          role: "system",
          content: `Renumbered. ${response.data.updatedLessonCount} lesson(s) and ${response.data.updatedQuizCount} quiz(zes) updated.`,
        },
      ]);
      if (typeof onRenumberApplied === "function") {
        onRenumberApplied(response.data.applied);
      }
    } catch (err) {
      const errors = err.response?.data?.errors;
      const errMsg = Array.isArray(errors) && errors.length
        ? errors.join("\n")
        : err.response?.data?.error || err.message || "Renumber failed.";
      setMessages((prev) => [
        ...prev.map((m, i) =>
          i === messageIndex ? { ...m, renumberStatus: "failed" } : m
        ),
        { role: "assistant", content: `Renumber failed:\n${errMsg}`, isError: true },
      ]);
    }
  };

  const handleCancelRenumber = (messageIndex) => {
    setMessages((prev) => [
      ...prev.map((m, i) =>
        i === messageIndex ? { ...m, renumberStatus: "cancelled" } : m
      ),
      { role: "system", content: "Renumber cancelled. Nothing was written." },
    ]);
  };

  const handleConfirmLabelRename = async (messageIndex) => {
    const target = messages[messageIndex];
    const plan = target?.labelRenamePlan;
    if (!plan || !selectedCourse || target.labelRenameStatus !== "pending") return;

    setMessages((prev) =>
      prev.map((m, i) => (i === messageIndex ? { ...m, labelRenameStatus: "applying" } : m))
    );

    try {
      const response = await api.post(`/${courseTitlePath}/renameCourseLabels`, {
        modelName: selectedCourse.courseTopicsModelName,
        collectionName: selectedCourse.courseTopicsCollectionName,
        sectionNumber: plan.updates?.sectionNumber || lesson?.sectionNumber,
        sectionTitle: plan.updates?.sectionTitle || undefined,
        topicTitle: plan.updates?.topicTitle || undefined,
        topicHeader: plan.updates?.topicHeader || undefined,
      });
      const chapterCount = response.data.updatedChapterCount || 0;
      const lessonCount = response.data.updatedLessonCount || 0;
      setMessages((prev) => [
        ...prev.map((m, i) =>
          i === messageIndex ? { ...m, labelRenameStatus: "confirmed" } : m
        ),
        {
          role: "system",
          content: `Renamed. ${lessonCount} lesson title(s) and ${chapterCount} chapter document(s) updated.`,
        },
      ]);
      if (typeof onLabelRenameApplied === "function") {
        onLabelRenameApplied(response.data.applied);
      }
    } catch (err) {
      const errors = err.response?.data?.errors;
      const errMsg = Array.isArray(errors) && errors.length
        ? errors.join("\n")
        : err.response?.data?.error || err.message || "Rename failed.";
      setMessages((prev) => [
        ...prev.map((m, i) =>
          i === messageIndex ? { ...m, labelRenameStatus: "failed" } : m
        ),
        { role: "assistant", content: `Rename failed:\n${errMsg}`, isError: true },
      ]);
    }
  };

  const handleCancelLabelRename = (messageIndex) => {
    setMessages((prev) => [
      ...prev.map((m, i) =>
        i === messageIndex ? { ...m, labelRenameStatus: "cancelled" } : m
      ),
      { role: "system", content: "Rename cancelled. Nothing was written." },
    ]);
  };

  const handleConfirmDelete = async (messageIndex) => {
    const target = messages[messageIndex];
    const plan = target?.deletePlan;
    if (!plan || !selectedCourse || target.deleteStatus !== "pending") return;

    setMessages((prev) =>
      prev.map((m, i) => (i === messageIndex ? { ...m, deleteStatus: "applying" } : m))
    );

    try {
      const response = await api.post(`/${courseTitlePath}/deleteCourseStructure`, {
        modelName: selectedCourse.courseTopicsModelName,
        collectionName: selectedCourse.courseTopicsCollectionName,
        quizModelName: selectedCourse.courseExamplesModelName,
        quizCollectionName: selectedCourse.courseExamplesCollectionName,
        refs: plan.refs,
        openSectionNumber: lesson?.sectionNumber,
      });
      const deletedLessons = response.data.deletedLessonCount || 0;
      const updatedLessons = response.data.updatedLessonCount || 0;
      const deletedQuizzes = response.data.deletedQuizCount || 0;
      setMessages((prev) => [
        ...prev.map((m, i) =>
          i === messageIndex ? { ...m, deleteStatus: "confirmed" } : m
        ),
        {
          role: "system",
          content: `Deleted. ${deletedLessons} lesson(s) removed, ${updatedLessons} lesson number(s) updated, ${deletedQuizzes} quiz(zes) removed.`,
        },
      ]);
      if (typeof onDeleteApplied === "function") {
        onDeleteApplied(response.data.applied);
      }
    } catch (err) {
      const errors = err.response?.data?.errors;
      const errMsg = Array.isArray(errors) && errors.length
        ? errors.join("\n")
        : err.response?.data?.error || err.message || "Delete failed.";
      setMessages((prev) => [
        ...prev.map((m, i) =>
          i === messageIndex ? { ...m, deleteStatus: "failed" } : m
        ),
        { role: "assistant", content: `Delete failed:\n${errMsg}`, isError: true },
      ]);
    }
  };

  const handleCancelDelete = (messageIndex) => {
    setMessages((prev) => [
      ...prev.map((m, i) =>
        i === messageIndex ? { ...m, deleteStatus: "cancelled" } : m
      ),
      { role: "system", content: "Delete cancelled. Nothing was written." },
    ]);
  };

  const handleConfirmImport = async (messageIndex) => {
    const target = messages[messageIndex];
    const plan = target?.importPlan;
    if (!plan || target.importStatus !== "pending") return;

    if (
      plan.baseSignature
      && animationSignature(lesson?.lessonAnimation) !== plan.baseSignature
    ) {
      setMessages((prev) => [
        ...prev.map((m, i) => (i === messageIndex ? { ...m, importStatus: "stale" } : m)),
        {
          role: "system",
          content: "The lesson changed since this was proposed — ask again.",
        },
      ]);
      return;
    }

    if (!plan.resultLesson) {
      setMessages((prev) => [
        ...prev.map((m, i) => (i === messageIndex ? { ...m, importStatus: "failed" } : m)),
        { role: "assistant", content: "Merge failed: the plan has no merged lesson.", isError: true },
      ]);
      return;
    }

    setMessages((prev) =>
      prev.map((m, i) => (i === messageIndex ? { ...m, importStatus: "applying" } : m))
    );

    try {
      let resultLesson = JSON.parse(JSON.stringify(plan.resultLesson));
      let resultQuizzes = Array.isArray(plan.resultQuizzes)
        ? JSON.parse(JSON.stringify(plan.resultQuizzes))
        : JSON.parse(JSON.stringify(quizzes || []));
      const imageUrls = plan.imageUrls || [];
      const lessonId = lesson.lessonId || nanoid();

      if (imageUrls.length > 0) {
        const response = await api.post("/api/copy-lesson-images", {
          urls: imageUrls,
          targetLessonId: lessonId,
          courseTitle: selectedCourse?.courseTitle,
        });
        const map = response.data.map || {};
        const failed = response.data.failed || [];
        if (failed.length > 0) {
          throw new Error(
            failed.map((f) => f.error || f.url).join("; ") || "Some images could not be copied."
          );
        }
        resultLesson = rewriteMediaUrls(resultLesson, map);
        resultQuizzes = rewriteMediaUrls(resultQuizzes, map);
      }

      resultLesson.lessonId = lesson.lessonId || lessonId;

      if (target.preMergeSnapshot) {
        setUndoStack((prev) => [...prev, target.preMergeSnapshot]);
      }

      onLessonUpdate(resultLesson);
      onQuizzesUpdate(resultQuizzes);

      const sourceNote = (plan.sourceLessons || [])
        .map((s) => {
          const title = s.sectionTitle ? ` "${s.sectionTitle}"` : "";
          return s.fullyCopied
            ? `${s.sectionNumber}${title} (now likely redundant)`
            : `${s.sectionNumber}${title}`;
        })
        .join("; ");

      setMessages((prev) => [
        ...prev.map((m, i) => (i === messageIndex ? { ...m, importStatus: "confirmed" } : m)),
        {
          role: "system",
          content: `Merge applied to the editor as unsaved changes. Press Save to DB to persist.${
            sourceNote ? ` Source lesson(s) were not modified: ${sourceNote}.` : ""
          }`,
        },
      ]);
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || "Merge failed.";
      setMessages((prev) => [
        ...prev.map((m, i) => (i === messageIndex ? { ...m, importStatus: "failed" } : m)),
        { role: "assistant", content: `Merge failed:\n${errMsg}`, isError: true },
      ]);
    }
  };

  const handleCancelImport = (messageIndex) => {
    setMessages((prev) => [
      ...prev.map((m, i) =>
        i === messageIndex ? { ...m, importStatus: "cancelled" } : m
      ),
      { role: "system", content: "Merge cancelled. The editor was not changed." },
    ]);
  };

  const updateMessage = (messageIndex, patch) =>
    setMessages((prev) =>
      prev.map((m, i) => (i === messageIndex ? { ...m, ...patch } : m))
    );

  const handleConfirmCourseEdit = async (messageIndex) => {
    const target = messages[messageIndex];
    const plan = target?.courseEditPlan;
    if (!plan || target.courseEditStatus !== "pending") return;
    if (typeof onCourseEditConfirmed !== "function") return;

    const total = (plan.targets || []).length;
    updateMessage(messageIndex, {
      courseEditStatus: "running",
      courseEditProgress: { edited: 0, unchanged: 0, failed: 0, skipped: 0, total },
    });

    try {
      const result = await onCourseEditConfirmed(plan, (progress) =>
        updateMessage(messageIndex, { courseEditProgress: progress })
      );
      const parts = [`Edited ${result.edited} of ${result.total} lesson(s).`];
      if (result.unchanged) parts.push(`${result.unchanged} needed no change.`);
      if (result.failed) parts.push(`${result.failed} failed.`);
      if (result.skipped) parts.push(`${result.skipped} skipped after Stop.`);
      if (result.edited) {
        parts.push("Edited lessons are marked Unsaved in the course nav. Open each one and press Save to DB to persist.");
      }
      setMessages((prev) => [
        ...prev.map((m, i) =>
          i === messageIndex
            ? { ...m, courseEditStatus: "done", courseEditProgress: result }
            : m
        ),
        { role: "system", content: parts.join(" ") },
      ]);
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || "Course edit failed.";
      setMessages((prev) => [
        ...prev.map((m, i) => (i === messageIndex ? { ...m, courseEditStatus: "failed" } : m)),
        { role: "assistant", content: `Course edit failed:\n${errMsg}`, isError: true },
      ]);
    }
  };

  const handleStopCourseEdit = (messageIndex) => {
    updateMessage(messageIndex, { courseEditStopping: true });
    if (typeof onCourseEditStop === "function") onCourseEditStop();
  };

  const handleCancelCourseEdit = (messageIndex) => {
    setMessages((prev) => [
      ...prev.map((m, i) =>
        i === messageIndex ? { ...m, courseEditStatus: "cancelled" } : m
      ),
      { role: "system", content: "Course edit cancelled. Nothing was changed." },
    ]);
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const last = undoStack[undoStack.length - 1];
    onLessonUpdate(last.lesson);
    onQuizzesUpdate(last.quizzes);
    setUndoStack((prev) => prev.slice(0, -1));
    setMessages((prev) => [
      ...prev,
      { role: "system", content: "↩ Reverted last agent change." },
    ]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!isThinking) sendMessage();
    }
  };

  const { prevLesson, nextLesson } = getAdjacentLessons();

  return (
    <>
      {/* Backdrop */}
      <div className={`agent-chat-backdrop ${isOpen ? "open" : ""}`} aria-hidden="true" />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`agent-chat-drawer ${isOpen ? "open" : ""}`}
        role="dialog"
        aria-label="AI Lesson Agent"
      >
        {/* Header */}
        <div className="agent-chat-header">
          <div className="agent-chat-header-info">
            <span className="agent-chat-title">AI Lesson Agent</span>
            {lesson && (
              <span className="agent-chat-subtitle">
                {lesson.sectionNumber} — {lesson.sectionTitle}
              </span>
            )}
          </div>
          <button
            className="agent-chat-close"
            onClick={onClose}
            aria-label="Close agent chat"
          >
            ✕
          </button>
        </div>

        {/* Context toggles */}
        <div className="agent-chat-context-bar">
          <div
            className="agent-context-toggle"
            onClick={() => prevLesson && setIncludePrev((prev) => !prev)}
          >
            <input
              type="checkbox"
              checked={includePrev}
              onChange={(e) => setIncludePrev(e.target.checked)}
              disabled={!prevLesson}
              onClick={(e) => e.stopPropagation()}
            />
            <span title={prevLesson ? `Prev: ${prevLesson.sectionTitle}` : "No previous lesson"}>
              ← Prev lesson
            </span>
          </div>
          <div
            className="agent-context-toggle"
            onClick={() => nextLesson && setIncludeNext((prev) => !prev)}
          >
            <input
              type="checkbox"
              checked={includeNext}
              onChange={(e) => setIncludeNext(e.target.checked)}
              disabled={!nextLesson}
              onClick={(e) => e.stopPropagation()}
            />
            <span title={nextLesson ? `Next: ${nextLesson.sectionTitle}` : "No next lesson"}>
              Next lesson →
            </span>
          </div>
          {undoStack.length > 0 && (
            <button
              className="agent-undo-button"
              onClick={handleUndo}
              disabled={isThinking}
              title={isThinking ? "Wait for the agent to finish or stop it first" : "Undo last AI change"}
            >
              ↩ Undo
            </button>
          )}
        </div>

        {/* Messages */}
        <div className="agent-chat-messages">
          {messages.length === 0 && (
            <div className="agent-chat-empty">
              <p>Ask me to make changes to this lesson or its quizzes.</p>
              <div className="agent-chat-examples">
                <button
                  className="agent-example-chip"
                  onClick={() => setInput("Simplify the language in all sections")}
                >
                  Simplify language
                </button>
                <button
                  className="agent-example-chip"
                  onClick={() => setInput("Make the quiz question harder")}
                >
                  Harder quiz
                </button>
                <button
                  className="agent-example-chip"
                  onClick={() => setInput("Regenerate the entire lesson")}
                >
                  Regenerate lesson
                </button>
                <button
                  className="agent-example-chip"
                  onClick={() => setInput("Generate images for all sections")}
                >
                  Generate images
                </button>
                <button
                  className="agent-example-chip"
                  onClick={() => setInput("Combine this lesson and the next lesson into one lesson, just add the content, don't change anything")}
                >
                  Combine with next
                </button>
                <button
                  className="agent-example-chip"
                  onClick={() => setInput("Replace the Story Book templates with other templates in every lesson that uses them")}
                >
                  Replace Story Book everywhere
                </button>
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`agent-message ${msg.role} ${msg.isError ? "error" : ""}`}
            >
              <div className="agent-message-role">
                {msg.role === "user" ? "You" : msg.role === "assistant" ? "Agent" : ""}
              </div>
              <div className="agent-message-content">
                {msg.content.split("\n").map((line, j) => (
                  <span key={j}>
                    {line}
                    {j < msg.content.split("\n").length - 1 && <br />}
                  </span>
                ))}
              </div>
              {msg.renumberPlan && (
                <div className="agent-renumber-card">
                  <div className="agent-renumber-title">Proposed renumber</div>
                  <ul className="agent-renumber-list">
                    {(msg.renumberPlan.summaryLines || []).map((line, j) => (
                      <li key={j}>{line}</li>
                    ))}
                  </ul>
                  {((msg.renumberPlan.warnings || []).length > 0
                    || (msg.renumberPlan.emptyChapters || []).length > 0) && (
                    <div className="agent-renumber-warnings">
                      {(msg.renumberPlan.emptyChapters || []).map((chapter, j) => (
                        <div key={`empty-${j}`}>
                          Leave empty for later deletion: {chapter.topicNumber}
                          {chapter.topicTitle ? ` — ${chapter.topicTitle}` : ""}
                        </div>
                      ))}
                      {(msg.renumberPlan.warnings || [])
                        .filter((warning) => !/will be left empty/i.test(warning))
                        .map((warning, j) => (
                          <div key={`warn-${j}`}>{warning}</div>
                        ))}
                    </div>
                  )}
                  {hasUnsavedChanges && msg.renumberStatus === "pending" && (
                    <div className="agent-renumber-unsaved">
                      This writes to the database immediately. Save unsaved lesson edits first if you want to keep them.
                    </div>
                  )}
                  {msg.renumberStatus === "pending" && (
                    <div className="agent-renumber-actions">
                      <button
                        type="button"
                        className="agent-renumber-confirm"
                        onClick={() => handleConfirmRenumber(i)}
                      >
                        Confirm renumber
                      </button>
                      <button
                        type="button"
                        className="agent-renumber-cancel"
                        onClick={() => handleCancelRenumber(i)}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  {msg.renumberStatus === "applying" && (
                    <div className="agent-renumber-status">Applying…</div>
                  )}
                  {msg.renumberStatus === "confirmed" && (
                    <div className="agent-renumber-status done">Applied.</div>
                  )}
                  {msg.renumberStatus === "cancelled" && (
                    <div className="agent-renumber-status">Cancelled.</div>
                  )}
                  {msg.renumberStatus === "failed" && (
                    <div className="agent-renumber-status failed">Failed — see the message below.</div>
                  )}
                </div>
              )}
              {msg.deletePlan && (
                <div className="agent-renumber-card">
                  <div className="agent-renumber-title">Proposed delete</div>
                  <ul className="agent-renumber-list">
                    {(msg.deletePlan.summaryLines || []).map((line, j) => {
                      const indent = (line.match(/^ */) || [""])[0].length;
                      return (
                        <li
                          key={j}
                          style={indent ? { marginLeft: indent * 6, listStyleType: "circle" } : undefined}
                        >
                          {line.trim()}
                        </li>
                      );
                    })}
                  </ul>
                  {hasUnsavedChanges && msg.deleteStatus === "pending" && (
                    <div className="agent-renumber-unsaved">
                      This writes to the database immediately. Save unsaved lesson edits first if you want to keep them.
                    </div>
                  )}
                  {msg.deleteStatus === "pending" && (
                    <div className="agent-renumber-actions">
                      <button
                        type="button"
                        className="agent-renumber-confirm"
                        onClick={() => handleConfirmDelete(i)}
                      >
                        Confirm delete
                      </button>
                      <button
                        type="button"
                        className="agent-renumber-cancel"
                        onClick={() => handleCancelDelete(i)}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  {msg.deleteStatus === "applying" && (
                    <div className="agent-renumber-status">Applying…</div>
                  )}
                  {msg.deleteStatus === "confirmed" && (
                    <div className="agent-renumber-status done">Applied.</div>
                  )}
                  {msg.deleteStatus === "cancelled" && (
                    <div className="agent-renumber-status">Cancelled.</div>
                  )}
                  {msg.deleteStatus === "failed" && (
                    <div className="agent-renumber-status failed">Failed — see the message below.</div>
                  )}
                </div>
              )}
              {msg.labelRenamePlan && (
                <div className="agent-renumber-card">
                  <div className="agent-renumber-title">Proposed rename</div>
                  <ul className="agent-renumber-list">
                    {(msg.labelRenamePlan.summaryLines || []).map((line, j) => {
                      const indent = (line.match(/^ */) || [""])[0].length;
                      return (
                        <li
                          key={j}
                          style={indent ? { marginLeft: indent * 6, listStyleType: "circle" } : undefined}
                        >
                          {line.trim()}
                        </li>
                      );
                    })}
                  </ul>
                  {hasUnsavedChanges && msg.labelRenameStatus === "pending" && (
                    <div className="agent-renumber-unsaved">
                      This writes to the database immediately. Save unsaved lesson edits first if you want to keep them.
                    </div>
                  )}
                  {msg.labelRenameStatus === "pending" && (
                    <div className="agent-renumber-actions">
                      <button
                        type="button"
                        className="agent-renumber-confirm"
                        onClick={() => handleConfirmLabelRename(i)}
                      >
                        Confirm rename
                      </button>
                      <button
                        type="button"
                        className="agent-renumber-cancel"
                        onClick={() => handleCancelLabelRename(i)}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  {msg.labelRenameStatus === "applying" && (
                    <div className="agent-renumber-status">Applying…</div>
                  )}
                  {msg.labelRenameStatus === "confirmed" && (
                    <div className="agent-renumber-status done">Applied.</div>
                  )}
                  {msg.labelRenameStatus === "cancelled" && (
                    <div className="agent-renumber-status">Cancelled.</div>
                  )}
                  {msg.labelRenameStatus === "failed" && (
                    <div className="agent-renumber-status failed">Failed — see the message below.</div>
                  )}
                </div>
              )}
              {msg.importPlan && (
                <div className="agent-renumber-card">
                  <div className="agent-renumber-title">Proposed merge</div>
                  <ul className="agent-renumber-list">
                    {(msg.importPlan.summaryLines || []).map((line, j) => (
                      <li key={j}>{line}</li>
                    ))}
                  </ul>
                  {msg.importStatus === "pending" && (
                    <div className="agent-renumber-unsaved">
                      Applies to the editor as unsaved changes — press Save to DB to persist.
                    </div>
                  )}
                  {msg.importStatus === "pending"
                    && msg.importPlan.baseSignature
                    && animationSignature(lesson?.lessonAnimation) !== msg.importPlan.baseSignature && (
                    <div className="agent-renumber-warnings">
                      The lesson changed since this was proposed — ask again.
                    </div>
                  )}
                  {msg.importStatus === "pending" && (
                    <div className="agent-renumber-actions">
                      <button
                        type="button"
                        className="agent-renumber-confirm"
                        onClick={() => handleConfirmImport(i)}
                        disabled={
                          !!msg.importPlan.baseSignature
                          && animationSignature(lesson?.lessonAnimation) !== msg.importPlan.baseSignature
                        }
                      >
                        Confirm merge
                      </button>
                      <button
                        type="button"
                        className="agent-renumber-cancel"
                        onClick={() => handleCancelImport(i)}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  {msg.importStatus === "applying" && (
                    <div className="agent-renumber-status">Copying images and applying…</div>
                  )}
                  {msg.importStatus === "confirmed" && (
                    <div className="agent-renumber-status done">Applied to the editor.</div>
                  )}
                  {msg.importStatus === "cancelled" && (
                    <div className="agent-renumber-status">Cancelled.</div>
                  )}
                  {msg.importStatus === "failed" && (
                    <div className="agent-renumber-status failed">Failed — see the message below.</div>
                  )}
                  {msg.importStatus === "stale" && (
                    <div className="agent-renumber-status failed">
                      The lesson changed since this was proposed — ask again.
                    </div>
                  )}
                </div>
              )}
              {msg.courseEditPlan && (
                <div className="agent-renumber-card">
                  <div className="agent-renumber-title">
                    Proposed course edit · {(msg.courseEditPlan.targets || []).length} lesson(s)
                  </div>
                  <div className="agent-course-edit-instruction">
                    {msg.courseEditPlan.instruction}
                  </div>
                  <ul className="agent-renumber-list">
                    {(msg.courseEditPlan.targets || []).map((t) => (
                      <li key={t.sectionNumber}>
                        {t.sectionNumber} — {t.sectionTitle || "Untitled"}
                        {t.reason ? ` (${t.reason})` : ""}
                        {t.isOpenLesson ? " · open lesson" : ""}
                      </li>
                    ))}
                  </ul>
                  {msg.courseEditStatus === "pending" && (
                    <div className="agent-renumber-unsaved">
                      Each lesson becomes an unsaved draft. Open it and press Save to DB to persist.
                    </div>
                  )}
                  {msg.courseEditStatus === "pending" && (
                    <div className="agent-renumber-actions">
                      <button
                        type="button"
                        className="agent-renumber-confirm"
                        onClick={() => handleConfirmCourseEdit(i)}
                      >
                        Confirm and edit {(msg.courseEditPlan.targets || []).length} lesson(s)
                      </button>
                      <button
                        type="button"
                        className="agent-renumber-cancel"
                        onClick={() => handleCancelCourseEdit(i)}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  {msg.courseEditStatus === "running" && msg.courseEditProgress && (
                    <div className="agent-renumber-actions">
                      <div className="agent-renumber-status">
                        Editing… {msg.courseEditProgress.edited + msg.courseEditProgress.unchanged + msg.courseEditProgress.failed + msg.courseEditProgress.skipped}
                        {" "}of {msg.courseEditProgress.total} done
                        {msg.courseEditProgress.failed ? `, ${msg.courseEditProgress.failed} failed` : ""}
                      </div>
                      <button
                        type="button"
                        className="agent-renumber-cancel"
                        onClick={() => handleStopCourseEdit(i)}
                        disabled={!!msg.courseEditStopping}
                      >
                        {msg.courseEditStopping ? "Stopping…" : "Stop"}
                      </button>
                    </div>
                  )}
                  {msg.courseEditStatus === "done" && (
                    <div className="agent-renumber-status done">
                      Finished. Edited lessons are marked Unsaved in the course nav.
                    </div>
                  )}
                  {msg.courseEditStatus === "cancelled" && (
                    <div className="agent-renumber-status">Cancelled.</div>
                  )}
                  {msg.courseEditStatus === "failed" && (
                    <div className="agent-renumber-status failed">Failed — see the message below.</div>
                  )}
                </div>
              )}
            </div>
          ))}

          {isThinking && (
            <div className="agent-message assistant thinking">
              <div className="agent-message-role">Agent</div>
              <div className="agent-thinking-row">
                <div className="agent-thinking-dots">
                  <span />
                  <span />
                  <span />
                </div>
                <button
                  type="button"
                  className="agent-chat-stop"
                  onClick={handleStop}
                  disabled={isStopping}
                  aria-label="Stop the agent"
                  title="Stop the agent — changes already applied are kept"
                >
                  {isStopping ? "Stopping…" : "Stop"}
                </button>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="agent-chat-input-area">
          <textarea
            ref={inputRef}
            className="agent-chat-input"
            placeholder="e.g. 'Simplify section 2', 'Change the quiz answer', 'Generate images'…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            readOnly={isThinking}
          />
          {isThinking ? (
            <button
              type="button"
              className="agent-chat-stop"
              onClick={handleStop}
              disabled={isStopping}
              aria-label="Stop the agent"
              title="Stop the agent — changes already applied are kept"
            >
              {isStopping ? "Stopping…" : "■ Stop"}
            </button>
          ) : (
            <button
              type="button"
              className="agent-chat-send"
              onClick={sendMessage}
              disabled={!input.trim()}
              aria-label="Send message"
            >
              Send
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default AgentChatPanel;
